import { escapeHtml } from './html';
import { renderProductObjectLink } from './product-object';
import { skillCertificationBadge } from './skill-certification';

export interface SkillListItem {
  readonly id: number;
  readonly name: string;
  readonly description?: string | null;
  readonly version?: string | null;
  readonly tags?: string | null;
  readonly files_count?: number | null;
  readonly entrypoint?: string | null;
  readonly download_count?: number | null;
  readonly feedback_count?: number | null;
  readonly well_known_skill_md_url?: string | null;
  readonly source_issue_id?: number | null;
  readonly metadata?: string | null;
  readonly certification_level?: string | null;
}

export function renderSkillTags(rawTags?: string | null): string {
  const tags = (rawTags || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
  if (!tags.length) return '';
  return tags
    .map((tag) => '<span class="px-2 py-0.5 rounded-full bg-surface-3 text-zinc-400 text-xs">#' + escapeHtml(tag) + '</span>')
    .join('');
}

export function renderSkillEmptyState(): string {
  return '' +
    '<div class="col-span-full card p-8 text-center text-zinc-500">' +
      '<i class="fas fa-puzzle-piece text-2xl mb-2"></i>' +
      '<p>No skills found.</p>' +
    '</div>';
}

export function renderSkillCard(skill: SkillListItem): string {
  const safeName = escapeHtml(skill.name);
  const safeDescription = escapeHtml((skill.description || '').trim() || 'No description provided.');
  const filesCount = Number(skill.files_count ?? 1);
  const entrypoint = skill.entrypoint ? escapeHtml(skill.entrypoint) : null;
  const popularity = Number(skill.download_count ?? 0);
  const feedbackCount = Number(skill.feedback_count ?? 0);
  const tagsHtml = renderSkillTags(skill.tags);
  const certBadge = skillCertificationBadge(skill.certification_level);
  const encodedName = encodeURIComponent(skill.name);
  const skillMdUrl = escapeHtml(skill.well_known_skill_md_url || '/.well-known/skills/' + encodedName + '/SKILL.md');
  const issueLink = skill.source_issue_id
    ? renderProductObjectLink({
        type: 'issue',
        id: skill.source_issue_id,
        label: 'Improved from #' + skill.source_issue_id,
        className: 'text-xs text-zinc-500 hover:text-brand-400',
      })
    : '';
  const version = skill.version ? '<span class="text-xs text-zinc-500">v' + escapeHtml(skill.version) + '</span>' : '';

  return '' +
    '<article class="card p-5 flex flex-col gap-3">' +
      '<div class="flex items-start justify-between gap-2">' +
        '<div class="flex min-w-0 flex-wrap items-center gap-2">' +
          '<a href="/skills/' + encodedName + '" class="font-semibold text-zinc-100 font-mono hover:text-brand-400">' + safeName + '</a>' +
          certBadge +
          version +
        '</div>' +
        '<div class="flex shrink-0 flex-col items-end gap-1 text-xs text-zinc-500">' +
          '<span title="Direct reads and downloads"><i class="fas fa-chart-line mr-1"></i>' + popularity + ' uses</span>' +
          '<span title="Agent feedback notes"><i class="fas fa-comment-dots mr-1"></i>' + feedbackCount + ' feedback</span>' +
        '</div>' +
      '</div>' +
      '<p class="text-sm text-zinc-400 leading-relaxed">' + safeDescription + '</p>' +
      (tagsHtml ? '<div class="flex flex-wrap gap-1.5">' + tagsHtml + '</div>' : '') +
      '<div class="text-xs text-zinc-600 space-y-0.5">' +
        '<div><span class="text-zinc-500">files:</span> ' + filesCount + ' &nbsp;' +
          (entrypoint ? '<span class="text-zinc-500">entrypoint:</span> <code class="text-zinc-400">' + entrypoint + '</code>' : '') +
        '</div>' +
      '</div>' +
      '<div class="flex flex-wrap gap-3 text-xs pt-1 border-t border-surface-3">' +
        '<a class="text-brand-400 hover:text-brand-300" href="/skills/' + encodedName + '">Details</a>' +
        '<a class="text-brand-400 hover:text-brand-300" href="' + skillMdUrl + '">Use SKILL.md</a>' +
        '<a class="text-brand-400 hover:text-brand-300" href="/api/skills/' + encodedName + '/download">Download zip</a>' +
        '<a class="text-brand-400 hover:text-brand-300" href="/api/skills/' + encodedName + '/package">Package</a>' +
        issueLink +
      '</div>' +
    '</article>';
}

export function renderSkillGrid(items: readonly SkillListItem[]): string {
  return items.length ? items.map(renderSkillCard).join('') : renderSkillEmptyState();
}
